'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * The stand-in drawn wherever an image has not been uploaded yet.
 *
 * A flat grey box with its own pixel size across the middle, which is what the
 * sponsored banner slots have always drawn for an unset banner. It is an
 * element rather than a file on purpose: a baked image has one fixed size that
 * gets cropped and scaled by whatever slot it lands in, so the number printed
 * on it stops matching the box it is sitting in. This measures itself, so the
 * figure is always the real size of the space waiting to be filled.
 *
 * Fills its nearest positioned ancestor, the same way `<Image fill>` does, so
 * it drops straight into the slots that were holding an image.
 */
export default function MediaPlaceholder({ className = '' }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ label: string; fontSize: number } | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      // Nothing to say while the box has no size yet (hidden tab, pre-layout).
      if (!width || !height) {
        setBox(null);
        return;
      }
      // Scale off the box rather than the viewport, so a small card and a full
      // bleed hero both get a label that fits them.
      const fontSize = Math.max(11, Math.min(width / 9, height / 3, 44));
      setBox({ label: `${Math.round(width)}x${Math.round(height)}`, fontSize });
    };

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden
      className={`absolute inset-0 flex select-none items-center justify-center bg-[#9CA3AF] ${className}`}
    >
      {box ? (
        <span
          className="px-3 text-center font-bold leading-none tracking-tight text-white"
          style={{ fontSize: `${box.fontSize}px` }}
        >
          {box.label}
        </span>
      ) : null}
    </div>
  );
}
